import React, { lazy, Suspense, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";

import { coreModalsConfig, coreStores } from "@components/coreModals";
import { buildModalsConfig } from "@custom/customModalsConfig.registry";

/**
 * Safely closes any store that has a `.close()` method in its state.
 */
const safeClose = (store) => {
  try {
    const s = store?.getState?.();
    if (typeof s?.close === "function") s.close();
  } catch (err) {
    console.warn("Safe close failed:", err);
  }
};

/**
 * 🔄 GlobalModals: Renders all core and custom modal components.
 * Automatically merges core + custom stores, and auto-closes all modals on route change.
 */
const GlobalModals = () => {
  const location = useLocation();

  // 🧩 Build registry & store map
  const { registry, customStores } = useMemo(() => {
    const ctx = {
      tenant: window.__TENANT__,
      env: process.env.NODE_ENV,
      flags: window.__FEATURE_FLAGS__ || {},
    };

    const strategy =
      process.env.REACT_APP_MODAL_OVERRIDE_STRATEGY || "by-priority";

    const reg = buildModalsConfig({ strategy, ctx });

    // Extract store functions from registry
    // const cStores = reg.reduce((acc, { key, store }) => {
    //   if (key && typeof store === "function") acc[key] = store;
    //   return acc;
    // }, {});
const storeMap = {};

reg.forEach((item) => {
  if (!item.key || typeof item.store !== "function") return;

  const existing = storeMap[item.key];

  if (!existing) {
    storeMap[item.key] = { store: item.store, override: item.override === true };
  } else {
    // Custom override wins
    if (item.override === true) {
      storeMap[item.key] = { store: item.store, override: true };
    }
    // Otherwise keep existing core one
  }
});

// final store map (flatten)
const customStores = Object.fromEntries(
  Object.entries(storeMap).map(([k, v]) => [k, v.store])
);


    return { registry: reg, customStores: customStores };
  }, []);

  // ⚙️ Lazy-load custom modal components
  const loadedCustomModals = useMemo(
    () =>
      registry.map(({ key, import: importFunc }) => ({
        key,
        Component: lazy(importFunc),
      })),
    [registry]
  );

  // 🧭 Create entity map (custom overrides core)
  const createEntityMap = useMemo(
    () => ({ ...coreStores, ...customStores }),
    [customStores]
  );

  // ✅ 👇 Add THIS useEffect here
  useEffect(() => {
    // Save globally once for reuse anywhere
    window.__GLOBAL_ENTITY_MAP__ = createEntityMap;
    console.log("[✅ Global Entity Map Registered]", Object.keys(createEntityMap));
  }, [createEntityMap]);

  // 🧹 Auto-close all open modals/stores on route change
  useEffect(() => {
    Object.values(coreStores).forEach(safeClose);
    Object.values(customStores).forEach(safeClose);
  }, [location.pathname, customStores]);

  const isOverridden = (key) =>
    registry.some((r) => String(r.key).toLowerCase() === String(key).toLowerCase());

  return (
    <div className="overlay-main-container">
      <Suspense
        fallback={
          <div className="flex items-center justify-center p-6 text-gray-500">
            <FaSpinner className="animate-spin" />
          </div>
        }
      >
        {loadedCustomModals.map(({ key, Component }) => (
          <Component key={`custom-${key}`} />
        ))}
      </Suspense>

      {coreModalsConfig.map(({ key, Component }) =>
        isOverridden(key) ? null : <Component key={`core-${key}`} />
      )}
    </div>
  );
};


export default GlobalModals;

/**
 * ✅ useCreateEntityMap — simple hook that just uses the built map.
 */
export const useCreateEntityMap = () => {
  // 👇 use the already created map directly
  return React.useMemo(() => {
    // Access the global createEntityMap instance from GlobalModals
    return window.__GLOBAL_ENTITY_MAP__ || {};
  }, []);
};
